import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  Box,
  PropertyFilter,
  PropertyFilterProps,
  CopyToClipboard,
  Popover,
  StatusIndicator,
  TableProps,
  Badge,
  Spinner,
} from "@amzn/awsui-components-console";
import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  Ard,
  UseCaseName,
} from "@amzn/fuse-accounting-central-datastore-client";
import ArdEditModal from "./ard-edit-modal";
import { ViewRawArdModal } from "./view-raw-ard-modal";
import { LoadingStatus } from "./jem-gen-content";
import { useJemGen } from "src/context/jem-gen/jemGenContext";
import {
  createShareableTinyUrl,
  evaluateQueryOnItem,
  getArdRowId,
  getStickyHeaderScrollOffset,
  STICKY_HEADER_VERTICAL_OFFSET,
} from "./ard-table-display.helper";
import { ModalMode } from "src/context/jem-gen/types";
import { BLANK_ARD, STANDARD_USE_CASE_NAMES } from "../constants/appConstants";
import { getLocalTime } from "src/pages/jemGen/components/split-panel.helper";
import { useFlareContext } from "src/context/flare/flareContext";
import {
  compareArdWithFlareConfig,
  countDiscrepancies,
  DiscrepancyCounts,
} from "src/context/flare/flare-comparison-service";

// Default preferences for the table
const ARD_DEFAULT_PREFERENCES = {
  pageSize: 50,
  visibleContent: [
    "id",
    "product",
    "template",
    "term",
    "billingMode",
    "businessCountryCode",
    "buyingCustomerCountryCode",
    "functionalCurrency",
    "localCurrency",
    "domain",
    "clientId",
    "deploymentStage",
    "usecases",
    "lastModifiedAt",
  ],
  wrapLines: false,
  custom: "table",
};

// Default query
const DEFAULT_QUERY: PropertyFilterProps.Query = {
  tokens: [],
  operation: "and",
};

// Pagination labels
const ardPaginationLabels = {
  nextPageLabel: "Next page",
  pageLabel: (pageNumber: number) => `Go to page ${pageNumber}`,
  previousPageLabel: "Previous page",
};

// Function to get matches count text
function getMatchesCountText(count: number) {
  return count === 1 ? `1 match` : `${count} matches`;
}

// Empty state component
function EmptyState({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action: React.ReactNode;
}) {
  return (
    <Box textAlign="center" color="inherit">
      <Box variant="strong" textAlign="center" color="inherit">
        {title}
      </Box>
      <Box variant="p" padding={{ bottom: "s" }} color="inherit">
        {subtitle}
      </Box>
      {action}
    </Box>
  );
}

interface ArdTableProps {
  loadingStatus: LoadingStatus;
  onRefresh: () => Promise<void>;
}

const ArdTableDisplay: React.FC<ArdTableProps> = ({
  loadingStatus,
  onRefresh,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [preferences, setPreferences] = useState(ARD_DEFAULT_PREFERENCES);
  const [isCollectionReady, setIsCollectionReady] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [sharePopoverContent, setSharePopoverContent] =
    useState<React.ReactElement | null>(null);
  const [viewRawModalVisible, setViewRawModalVisible] =
    useState<boolean>(false);

  // Capture the initial selectedArdId from URL when component mounts
  // This is used to determine if we should scroll on initial load
  const [initialSelectedArdId] = useState<string | null>(() =>
    searchParams.get("selectedArdId"),
  );

  // State to track if initial navigation to selected ARD has been performed
  const [hasScrolledToSelectedArd, setHasScrolledToSelectedArd] =
    useState<boolean>(false);

  // Use the ARD selection context
  const {
    existingArds,
    selectedArd,
    setSelectedArd,
    selectedArdDetailed,
    setSelectedArdDetailed,
    ardRegionMap,
    modalMode,
    setModalMode,
    setSplitPanelSize,
  } = useJemGen();

  // Use Flare context for comparisons
  const { flareConfig } = useFlareContext();

  // Calculate discrepancies for all ARDs
  const ardDiscrepanciesMap = useMemo<Map<string, DiscrepancyCounts>>(() => {
    const discrepanciesMap = new Map<string, DiscrepancyCounts>();

    if (!flareConfig) {
      return discrepanciesMap;
    }

    console.log(
      `Starting discrepancies calculation for ${existingArds.length} ARDs...`,
    );
    const startTime = performance.now();

    existingArds.forEach((ard) => {
      if (ard.id) {
        const comparison = compareArdWithFlareConfig(ard, flareConfig);
        const counts = countDiscrepancies(comparison);
        discrepanciesMap.set(ard.id, counts);
      }
    });

    const endTime = performance.now();
    console.log(
      `Discrepancies calculation completed in ${(endTime - startTime).toFixed(2)}ms`,
    );

    return discrepanciesMap;
  }, [existingArds, flareConfig]);

  const getQueryFromURL = useCallback((): PropertyFilterProps.Query => {
    const queryParam = searchParams.get("query");
    if (queryParam) {
      try {
        const parsedQuery = JSON.parse(queryParam);

        if (
          !parsedQuery ||
          !Array.isArray(parsedQuery.tokens) ||
          typeof parsedQuery.operation !== "string"
        ) {
          throw new Error("Invalid query in URL");
        }

        return parsedQuery as PropertyFilterProps.Query;
      } catch (e) {
        console.error("Failed to parse query from URL:", e);
      }
    }
    return DEFAULT_QUERY;
  }, [searchParams]);

  const [query, setQuery] =
    useState<PropertyFilterProps.Query>(getQueryFromURL);

  const updateURLQuery = useCallback(
    (newQuery: PropertyFilterProps.Query) => {
      const newSearchParams = new URLSearchParams(searchParams);

      if (newQuery.tokens.length > 0) {
        newSearchParams.set("query", JSON.stringify(newQuery));
      } else {
        newSearchParams.delete("query");
      }

      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const updateURLSelectedArd = useCallback(
    (ard: Ard | null) => {
      const newSearchParams = new URLSearchParams(searchParams);

      if (ard && ard.id) {
        newSearchParams.set("selectedArdId", ard.id);
      } else {
        newSearchParams.delete("selectedArdId");
      }

      // Preserve the hash when updating search params
      const currentHash = window.location.hash;
      setSearchParams(newSearchParams, { replace: true });
      if (currentHash) {
        window.history.replaceState(
          null,
          "",
          window.location.pathname +
            "?" +
            newSearchParams.toString() +
            currentHash,
        );
      }
    },
    [searchParams, setSearchParams],
  );

  const handleQueryChange = useCallback(
    (newQuery: PropertyFilterProps.Query) => {
      setQuery(newQuery);
      updateURLQuery(newQuery);
    },
    [updateURLQuery],
  );

  const handleArdSelectionChange = useCallback(
    (ard: Ard | null) => {
      setSelectedArd(ard);
      updateURLSelectedArd(ard);
    },
    [setSelectedArd, updateURLSelectedArd],
  );

  useEffect(() => {
    if (!selectedArd || !selectedArd.id) {
      const selectedArdId = searchParams.get("selectedArdId");
      if (selectedArdId) {
        const foundArd =
          existingArds.find((ard) => ard.id === selectedArdId) || null;
        setSelectedArd(foundArd);
        // Set split panel to max size when ARD is selected from URL
        setSplitPanelSize(window.innerHeight * 0.8);
      }
      return;
    }
    setSelectedArd(
      existingArds.find((ard: Ard) => ard.id === selectedArd.id) || null,
    );
  }, [existingArds]);

  const openCreateArdModal = () => {
    setSelectedArd(BLANK_ARD);
    setModalVisible(true);
    setModalMode(ModalMode.CREATE);
  };

  const handleDuplicateArd = (item: any) => {
    setSelectedArd(item);
    setModalMode(ModalMode.DUPLICATE);
    setModalVisible(true);
  };

  const handleEditArd = (item: any) => {
    setSelectedArd(item);
    setModalMode(ModalMode.EDIT);
    setModalVisible(true);
  };

  // Property filter configuration
  const filteringProperties: PropertyFilterProps.FilteringProperty[] = [
    {
      key: "flareDiscrepancies",
      operators: [
        {
          operator: "=",
          tokenType: "enum",
        },
      ],
      propertyLabel: "Flare Discrepancies",
      groupValuesLabel: "Flare Discrepancies values",
    },
    {
      key: "id",
      operators: ["=", "!="],
      propertyLabel: "ARD ID",
      groupValuesLabel: "ARD ID values",
    },
    {
      key: "product",
      operators: ["=", "!="],
      propertyLabel: "Product",
      groupValuesLabel: "Product values",
    },
    {
      key: "template",
      operators: ["=", "!="],
      propertyLabel: "Template",
      groupValuesLabel: "Template values",
    },
    {
      key: "term",
      operators: [
        {
          operator: "=",
          tokenType: "enum",
        },
        {
          operator: "!=",
          tokenType: "enum",
        },
      ],
      propertyLabel: "Term",
      groupValuesLabel: "Term values",
    },
    {
      key: "billingMode",
      operators: [
        {
          operator: "=",
          tokenType: "enum",
        },
        {
          operator: "!=",
          tokenType: "enum",
        },
      ],
      propertyLabel: "Billing Mode",
      groupValuesLabel: "Billing Mode values",
    },
    {
      key: "businessCountryCode",
      operators: ["=", "!="],
      propertyLabel: "Business Country",
      groupValuesLabel: "Business Country values",
    },
    {
      key: "buyingCustomerCountryCode",
      operators: ["=", "!="],
      propertyLabel: "Customer Country",
      groupValuesLabel: "Customer Country values",
    },
    {
      key: "functionalCurrency",
      operators: ["=", "!="],
      propertyLabel: "Functional Currency",
      groupValuesLabel: "Functional Currency values",
    },
    {
      key: "localCurrency",
      operators: ["=", "!="],
      propertyLabel: "Local Currency",
      groupValuesLabel: "Local Currency values",
    },
    {
      key: "usecaseName",
      operators: [
        {
          operator: "=",
          tokenType: "enum",
        },
        {
          operator: "!=",
          tokenType: "enum",
        },
      ],
      propertyLabel: "Use Case Name",
      groupValuesLabel: "Use Case Name values",
    },
  ];

  // Get unique values for property filter
  const getPropertyFilterOptions = (
    propertyKey: string,
  ): PropertyFilterProps.FilteringOption[] => {
    // Handle flareDiscrepancies property with specific options
    if (propertyKey === "flareDiscrepancies") {
      return [
        { propertyKey, value: "Has Mismatches" },
        { propertyKey, value: "Has Missing Values" },
      ];
    }

    // Handle usecaseName property
    if (propertyKey === "usecaseName") {
      return STANDARD_USE_CASE_NAMES.map((value) => ({
        propertyKey,
        value,
      }));
    }

    const uniqueValues = new Set<string>();
    existingArds.forEach((item) => {
      const value = item[propertyKey as keyof Ard];
      if (value && typeof value === "string") {
        uniqueValues.add(value);
      }
    });
    return Array.from(uniqueValues)
      .sort()
      .map((value) => ({ propertyKey, value }));
  };

  const ARD_COLUMN_DEFINITIONS: ReadonlyArray<
    TableProps.ColumnDefinition<Ard>
  > = [
    {
      id: "id",
      header: "ARD ID",
      cell: (item: Ard) => {
        const discrepancies = item.id
          ? ardDiscrepanciesMap.get(item.id)
          : undefined;
        return (
          <div id={getArdRowId(item.id || "")}>
            <SpaceBetween direction="horizontal" size="xs">
              <CopyToClipboard
                copyButtonAriaLabel="Copy ARD ID"
                copyErrorText="ARD ID failed to copy"
                copySuccessText="ARD ID copied"
                textToCopy={item.id || ""}
                variant="inline"
              />
              {(discrepancies && (
                <Popover
                  header="Flare Discrepancies"
                  renderWithPortal
                  content={`${discrepancies.mismatches} mismatches, ${discrepancies.missing} missing values, and ${discrepancies.matches} matches`}
                >
                  <Badge color="red">{discrepancies.mismatches}</Badge>
                  <Badge color="severity-low">{discrepancies.missing}</Badge>
                  <Badge color="green">{discrepancies.matches}</Badge>
                </Popover>
              )) || <Spinner />}
            </SpaceBetween>
          </div>
        );
      },
      sortingField: "id",
      minWidth: 450,
    },
    {
      id: "product",
      header: "Product",
      cell: (item: Ard) => item.product,
      sortingField: "product",
      minWidth: 150,
    },
    {
      id: "template",
      header: "Template",
      cell: (item: Ard) => item.template,
      sortingField: "template",
      minWidth: 150,
    },
    {
      id: "term",
      header: "Term",
      cell: (item: Ard) => item.term,
      sortingField: "term",
      minWidth: 150,
    },
    {
      id: "billingMode",
      header: "Billing Mode",
      cell: (item: Ard) => item.billingMode,
      sortingField: "billingMode",
      minWidth: 180,
    },
    {
      id: "businessCountryCode",
      header: "Business Country",
      cell: (item: Ard) => item.businessCountryCode,
      sortingField: "businessCountryCode",
      minWidth: 160,
    },
    {
      id: "buyingCustomerCountryCode",
      header: "Customer Country",
      cell: (item: Ard) => item.buyingCustomerCountryCode,
      sortingField: "buyingCustomerCountryCode",
      minWidth: 160,
    },
    {
      id: "functionalCurrency",
      header: "Functional Currency",
      cell: (item: Ard) => item.functionalCurrency,
      sortingField: "functionalCurrency",
      minWidth: 170,
    },
    {
      id: "localCurrency",
      header: "Local Currency",
      cell: (item: Ard) => item.localCurrency,
      sortingField: "localCurrency",
      minWidth: 160,
    },
    {
      id: "domain",
      header: "Domain",
      cell: (item: Ard) => (item.id && ardRegionMap.get(item.id)) || "N/A",
      minWidth: 120,
    },
    // {
    //   id: 'clientId',
    //   header: 'Client ID',
    //   cell: (item: Ard) => item.clientId,
    //   sortingField: 'clientId',
    //   minWidth: 100
    // },
    // {
    //   id: 'deploymentStage',
    //   header: 'Deployment Stage',
    //   cell: (item: Ard) => item.deploymentStage || '-',
    //   sortingField: 'deploymentStage',
    //   minWidth: 120
    // },
    {
      id: "usecases",
      header: "Use Cases",
      cell: (item: Ard) => {
        const usecases = item.usecases || [];
        return usecases.map((usecase) => usecase.name).join(", ");
      },
      minWidth: 150,
    },
    {
      id: "lastModifiedAt",
      header: "Last Modified At",
      cell: (item: Ard) => getLocalTime(item.changeAudit?.changedAt),
      minWidth: 150,
      sortingField: "changeAudit.changedAt",
      sortingComparator: (a: Ard, b: Ard) => {
        const dateA = a.changeAudit?.changedAt;
        const dateB = b.changeAudit?.changedAt;

        // Handle null/undefined values
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1; // Put items without dates at the end
        if (!dateB) return -1; // Put items without dates at the end

        return dateA.localeCompare(dateB);
      },
    },
  ];

  useEffect(() => {
    // Focus is now handled by the Input component
  }, []);

  const clearFilter = () => {
    handleQueryChange({ tokens: [], operation: "and" });
    actions.setFiltering("");
  };

  const {
    items,
    allPageItems,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(existingArds, {
    filtering: {
      empty: (
        <EmptyState
          title="No ARDs"
          subtitle="No ARDs to display."
          action={null}
        />
      ),
      noMatch: (
        <EmptyState
          title="No matches"
          subtitle="We can't find a match."
          action={<Button onClick={clearFilter}>Clear all filters</Button>}
        />
      ),
      filteringFunction: (item: Ard) =>
        evaluateQueryOnItem(query, item, ardDiscrepanciesMap),
    },
    pagination: {
      pageSize: preferences.pageSize,
    },
    sorting: {},
    selection: {},
  });

  // Effect to detect when collection processing is complete
  useEffect(() => {
    if (loadingStatus === "finished" && existingArds.length > 0) {
      // Check if filteredItemsCount is available and items array is populated
      if (filteredItemsCount !== undefined && items.length >= 0) {
        setIsCollectionReady(true);
      }
    } else if (loadingStatus === "loading") {
      setIsCollectionReady(false);
    } else if (loadingStatus === "error") {
      // On error, set collection as ready to stop showing loading state
      setIsCollectionReady(true);
    }
  }, [loadingStatus, existingArds.length, filteredItemsCount, items.length]);

  // Effect to navigate to the correct page and scroll to the selected ARD row (only on initial page load)
  // Uses allPageItems (filtered items from useCollection) to calculate page number when filters are applied
  // Only scrolls if there was a selectedArdId in the URL when the page first loaded
  useEffect(() => {
    // Only scroll if there was a selectedArdId in the URL on initial page load
    if (!initialSelectedArdId) {
      return;
    }

    if (
      !selectedArd ||
      !selectedArd.id ||
      !isCollectionReady ||
      hasScrolledToSelectedArd
    ) {
      return;
    }

    // Find the index in filtered items using allPageItems
    const ardIndex = allPageItems.findIndex(
      (ard) => ard.id === initialSelectedArdId,
    );
    if (ardIndex === -1) {
      return;
    }

    // Mark that we've scrolled to the selected ARD
    setHasScrolledToSelectedArd(true);

    const targetPage = Math.floor(ardIndex / preferences.pageSize) + 1;
    // Set the current page to where the ARD is located in filtered results
    actions.setCurrentPage(targetPage);

    // Scroll to the selected ARD row after a short delay to allow table to render
    setTimeout(() => {
      const element = document.getElementById(
        getArdRowId(initialSelectedArdId),
      );
      if (element) {
        // Get the element's position and scroll with offset for sticky header
        const rect = element.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        window.scrollTo({
          top:
            rect.top +
            scrollTop -
            getStickyHeaderScrollOffset(query.tokens.length > 0),
          behavior: "smooth",
        });
      }
    }, 100);
  }, [
    selectedArd,
    isCollectionReady,
    allPageItems,
    hasScrolledToSelectedArd,
    initialSelectedArdId,
  ]);

  const getTinyUrl = useCallback(async () => {
    try {
      setSharePopoverContent(
        <StatusIndicator type="loading">Generating Tiny URL</StatusIndicator>,
      );
      const tinyUrl = await createShareableTinyUrl();
      await navigator.clipboard.writeText(tinyUrl);
      setSharePopoverContent(
        <StatusIndicator type="success">URL Copied</StatusIndicator>,
      );
    } catch (error) {
      console.error("Failed to copy URL to clipboard:", error);
      setSharePopoverContent(
        <StatusIndicator type="error">Failed to copy URL</StatusIndicator>,
      );
    }
  }, []);

  const isArdActionDisabled = !selectedArd || loadingStatus !== "finished";

  return (
    <>
      <Table
        {...collectionProps}
        visibleColumns={preferences.visibleContent}
        selectionType="single"
        selectedItems={selectedArd ? [selectedArd] : []}
        onSelectionChange={({ detail }) => {
          const selectedItems = detail.selectedItems;
          const newSelectedArd =
            selectedItems.length > 0 ? selectedItems[0] : null;
          handleArdSelectionChange(newSelectedArd);
        }}
        wrapLines={preferences.wrapLines}
        loading={loadingStatus === "loading" || !isCollectionReady}
        loadingText={
          loadingStatus === "loading" ? "Loading ARDs" : "Processing ARDs"
        }
        stickyColumns={{ first: 1, last: 0 }}
        stickyHeader
        stickyHeaderVerticalOffset={STICKY_HEADER_VERTICAL_OFFSET}
        variant="full-page"
        header={
          <Header
            variant="h2"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="normal"
                  iconName="refresh"
                  onClick={() => {
                    setSelectedArdDetailed(null);
                    onRefresh();
                  }}
                  disabled={!["finished", "error"].includes(loadingStatus)}
                />
                <Popover
                  dismissButton={false}
                  position="top"
                  size="small"
                  triggerType="custom"
                  content={sharePopoverContent}
                >
                  <Button
                    iconName="share"
                    onClick={getTinyUrl}
                    ariaLabel="Share search query"
                  >
                    Copy deep URL
                  </Button>
                </Popover>
                <Button
                  variant="normal"
                  iconName="search"
                  ariaLabel={
                    selectedArd
                      ? `Apply filters from ARD ${selectedArd.id}`
                      : "Apply filters from ARD"
                  }
                  onClick={() => {
                    if (selectedArd) {
                      const tokens = [
                        {
                          propertyKey: "product",
                          operator: "=",
                          value: selectedArd.product,
                        },
                        {
                          propertyKey: "term",
                          operator: "=",
                          value: selectedArd.term,
                        },
                        {
                          propertyKey: "billingMode",
                          operator: "=",
                          value: selectedArd.billingMode,
                        },
                        {
                          propertyKey: "buyingCustomerCountryCode",
                          operator: "=",
                          value: selectedArd.buyingCustomerCountryCode,
                        },
                      ].filter((t) => !!t.value) as PropertyFilterProps.Token[];

                      const newQuery: PropertyFilterProps.Query = {
                        tokens,
                        operation: "and",
                      };
                      handleQueryChange(newQuery);
                      actions.setFiltering("");
                    }
                  }}
                  disabled={isArdActionDisabled}
                >
                  Apply Filters
                </Button>
                <Button
                  variant="normal"
                  iconName="copy"
                  ariaLabel={
                    selectedArd
                      ? `Duplicate ARD ${selectedArd.id}`
                      : "Duplicate ARD"
                  }
                  onClick={() => selectedArd && handleDuplicateArd(selectedArd)}
                  disabled={isArdActionDisabled}
                >
                  Duplicate
                </Button>
                <Button
                  variant="normal"
                  iconName="edit"
                  ariaLabel={
                    selectedArd ? `Edit ARD ${selectedArd.id}` : "Edit ARD"
                  }
                  onClick={() => selectedArd && handleEditArd(selectedArd)}
                  disabled={isArdActionDisabled}
                >
                  Edit
                </Button>
                <Button
                  variant="normal"
                  iconName="script"
                  ariaLabel={
                    selectedArd
                      ? `View raw ARD ${selectedArd.id}`
                      : "View raw ARD"
                  }
                  onClick={() => selectedArd && setViewRawModalVisible(true)}
                  disabled={isArdActionDisabled}
                >
                  View raw
                </Button>
                <Button
                  variant="primary"
                  onClick={openCreateArdModal}
                  disabled={loadingStatus !== "finished"}
                >
                  Create new ARD
                </Button>
              </SpaceBetween>
            }
            counter={`(${existingArds.length})`}
          >
            ARDs
          </Header>
        }
        columnDefinitions={ARD_COLUMN_DEFINITIONS}
        items={items}
        pagination={
          <Pagination {...paginationProps} ariaLabels={ardPaginationLabels} />
        }
        filter={
          <PropertyFilter
            {...filterProps}
            query={query}
            onChange={({ detail }) => handleQueryChange(detail)}
            filteringProperties={filteringProperties}
            filteringOptions={filteringProperties.reduce<
              PropertyFilterProps.FilteringOption[]
            >(
              (acc, property) =>
                acc.concat(getPropertyFilterOptions(property.key)),
              [],
            )}
            countText={getMatchesCountText(filteredItemsCount ?? 0)}
            expandToViewport={true}
            filteringAriaLabel="Find ARDs"
            filteringPlaceholder="Find ARDs"
            freeTextFiltering={{
              operators: [":"],
              defaultOperator: ":",
            }}
            i18nStrings={{
              enteredTextLabel: (value) => `Contains: ${value}`,
              groupPropertiesText: "Properties",
              groupValuesText: "Values",
              propertyText: "Property",
              valueText: "Value",
              operatorText: "Operator",
              cancelActionText: "Cancel",
              applyActionText: "Apply",
              operationAndText: "and",
              operationOrText: "or",
              clearFiltersText: "Clear filters",
              operatorContainsText: "Contains",
              operatorDoesNotContainText: "Does not contain",
              operatorEqualsText: "Equals",
              operatorDoesNotEqualText: "Does not equal",
              allPropertiesLabel: "All properties",
            }}
          />
        }
      />

      <ArdEditModal
        visible={modalVisible}
        onDismiss={() => {
          setModalVisible(false);
          if (modalMode !== ModalMode.EDIT) {
            setSelectedArd(null);
          }
        }}
        onRefresh={onRefresh}
        modalMode={modalMode}
      />

      <ViewRawArdModal
        visible={viewRawModalVisible}
        onDismiss={() => setViewRawModalVisible(false)}
        selectedArdDetailed={selectedArdDetailed}
        selectedArd={selectedArd}
      />
    </>
  );
};

export default ArdTableDisplay;
